import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ArrowRight, Calendar, FileSignature, Zap, Star, Puzzle, Workflow, Mic,
  Ticket, CheckSquare, Binoculars, Sparkles, Check,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";
import heroImg from "@/assets/hero-card.webp";

export const Route = createFileRoute("/platform/run-your-business")({
  head: () => ({
    meta: [
      { title: "Run Your Business — banana.cards" },
      { name: "description", content: "Bookings, meetings, contracts, reviews, pipelines, tasks, events and prospecting — every business app, on Banana." },
      { property: "og:title", content: "Run Your Business — banana.cards" },
      { property: "og:description", content: "Bookings, meetings, contracts, reviews, pipelines, tasks, events and prospecting — every business app, on Banana." },
      { property: "og:image", content: heroImg },
      { property: "og:url", content: "https://banana.cards/platform/run-your-business" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/platform/run-your-business" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Run Your Business — banana.cards",
          description: "Bookings, meetings, contracts, reviews, pipelines, tasks, events and prospecting — every business app, on Banana.",
          url: "https://banana.cards/platform/run-your-business",
          isPartOf: { "@type": "WebSite", name: "banana.cards", url: "https://banana.cards" },
        }),
      },
    ],
  }),
  component: Page,
});

type App = {
  title: string; tag: string; desc: string; href: string; icon: LucideIcon;
  bullets: string[]; stat?: { value: string; label: string };
};

const apps: App[] = [
  { title: "Bookings", tag: "Scheduling", desc: "Let clients book your time 24/7. Define services, durations, buffers, availability and accept payments at booking.", href: "/platform/bookings", icon: Calendar,
    bullets: ["Custom services & pricing", "Group, 1:1 and recurring slots", "Stripe payments at checkout", "Auto reminders & confirmations"],
    stat: { value: "24/7", label: "Self-serve booking" } },
  { title: "Contracts & e-Sign", tag: "Paperwork", desc: "Build reusable templates, fill placeholders, send for legally binding e-signature and track every status from sent to signed.", href: "/platform/contracts", icon: FileSignature,
    bullets: ["Reusable smart templates", "Placeholder variables per client", "Audit trail & signed PDFs", "Reminders for unsigned docs"],
    stat: { value: "e-Sign", label: "Legally binding" } },
  { title: "Smart Meetings", tag: "AI notetaker", desc: "Join calls with an AI notetaker that records, transcribes and summarizes — every decision and action item captured automatically.", href: "/platform/meetings", icon: Mic,
    bullets: ["Auto-join Zoom, Meet, Teams", "Searchable transcripts", "AI summary + action items", "Share with the team in a click"],
    stat: { value: "AI", label: "Notes & summaries" } },
  { title: "CRM", tag: "Contacts", desc: "A clean, fast CRM that captures every tap, scan and lead — with custom fields, tags, notes and full activity timeline.", href: "/platform/crm", icon: Sparkles,
    bullets: ["Auto-capture from card taps", "Custom fields & tags", "Activity timeline per contact", "Bulk actions & smart lists"],
    stat: { value: "1 inbox", label: "Every lead, organized" } },
  { title: "Pipelines", tag: "Deals", desc: "Kanban deal stages with custom fields, stage requirements and automations — keep work moving without the spreadsheet chaos.", href: "/platform/pipelines", icon: Workflow,
    bullets: ["Drag-and-drop kanban", "Stage requirements & gates", "Custom deal fields", "Forecast & weighted value"],
    stat: { value: "Kanban", label: "Visual pipeline" } },
  { title: "Tasks", tag: "Get it done", desc: "Assign, schedule and complete to-dos for yourself and your team — linked to contacts, deals and meetings so nothing falls through.", href: "/platform/tasks", icon: CheckSquare,
    bullets: ["Personal & team tasks", "Due dates & reminders", "Linked to contacts/deals", "Recurring & subtasks"],
    stat: { value: "0", label: "Balls dropped" } },
  { title: "Scout", tag: "AI prospecting", desc: "Find and qualify prospects from Google Places using natural language. Describe your ideal customer — Scout builds the list.", href: "/platform/scout", icon: Binoculars,
    bullets: ["Natural-language search", "Emails, sites & socials", "Enrichment & scoring", "Export to CRM or pipeline"],
    stat: { value: "AI", label: "Prospect on autopilot" } },
  { title: "AI Suite", tag: "Built-in AI", desc: "A suite of AI tools woven into every app — drafting, summarizing, enriching, replying and recommending the next best action.", href: "/platform/ai-suite", icon: Zap,
    bullets: ["AI drafts & replies", "Auto-enrichment", "Smart next actions", "Works across every app"],
    stat: { value: "10x", label: "Faster ops" } },
];

const more = [
  { title: "Reviews", desc: "Send review requests and showcase 5-star social proof on your card.", href: "/platform/reviews", icon: Star },
  { title: "Events & RSVPs", desc: "Create event pages, manage RSVPs and check guests in at the door.", href: "/platform/events", icon: Ticket },
  { title: "Automations", desc: "Triggers, actions and integrations that work while you sleep.", href: "/platform/automations", icon: Zap },
  { title: "Integrations", desc: "Plug into Google, Outlook, Zapier and 50+ tools.", href: "/platform/integrations", icon: Puzzle },
];

function Page() {
  return (
    <SiteLayout>
      {/* HERO — bg-background */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName="Run your business" />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              The business apps, <em className="italic">all together.</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">
              Install only what you need. Bookings, meetings, contracts, CRM, pipelines, tasks, prospecting and AI — all under one login, all talking to each other.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
                Get started — free <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/pricing" className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70">
                See pricing
              </Link>
            </div>
            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { v: "8+", l: "Apps included" },
                { v: "1", l: "Login & bill" },
                { v: "50+", l: "Integrations" },
              ].map((s) => (
                <div key={s.l}>
                  <dt className="font-display text-2xl">{s.v}</dt>
                  <dd className="mt-1 text-xs text-muted-foreground">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative order-1 md:order-2">
            <div className="absolute -inset-6 rounded-[2rem] bg-brand-lime/10 blur-3xl" aria-hidden />
            <img src={heroImg} alt="banana.cards run-your-business dashboard combining CRM, bookings, contracts and analytics for founders" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      {/* QUICK GRID OVERVIEW — bg-muted */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">What's inside</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Every tool, <em className="italic">at a glance.</em>
            </h2>
            <p className="mt-4 text-muted-foreground">Tap any app to jump to its deep-dive below — or open the dedicated page.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {apps.map((f) => {
              const Icon = f.icon;
              return (
                <a key={f.href} href={`#${slug(f.title)}`} className="group flex flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc.split(".")[0]}.</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                    Jump to <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* DEEP-DIVE SECTIONS — alternating */}
      {apps.map((app, i) => (
        <DeepDive key={app.href} app={app} flip={i % 2 === 1} muted={i % 2 === 1} />
      ))}

      {/* MORE — bg-muted */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">And more</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              The supporting cast.
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {more.map((m) => {
              const Icon = m.icon;
              return (
                <Link key={m.href} to={m.href} aria-label={`${m.title} — learn more about this feature`} className="group flex flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{m.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                    Learn more <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUTRO — bg-background with black accent box */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              One platform. <em className="italic text-brand-lime">Every workflow.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Install the apps you need today, switch on the rest as you grow.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Get started — free
              </CtaLink>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Talk to sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function slug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function DeepDive({ app, flip, muted }: { app: App; flip: boolean; muted: boolean }) {
  const Icon = app.icon;
  const sectionBg = muted ? "bg-muted" : "bg-background";
  const boxBg = muted ? "bg-card" : "bg-muted";
  return (
    <section id={slug(app.title)} className={sectionBg}>
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:px-6">
        <div className={flip ? "md:order-2" : ""}>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-foreground text-background">
              <Icon className="h-5 w-5" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{app.tag}</p>
          </div>
          <h2 className="mt-5 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            {app.title}
          </h2>
          <p className="mt-4 max-w-lg text-base text-muted-foreground md:text-lg">{app.desc}</p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 max-w-lg">
            {app.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to={app.href} className="inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
              Explore {app.title} <ArrowRight className="h-4 w-4" />
            </Link>
            <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl px-2 py-3 text-sm font-bold underline-offset-4 hover:underline">
              Try it free
            </CtaLink>
          </div>
        </div>
        <div className={flip ? "md:order-1" : ""}>
          <div className={`rounded-3xl ${boxBg} p-8 md:p-10`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{app.tag}</span>
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
            {app.stat && (
              <div className="mt-6">
                <div className="font-display text-5xl md:text-7xl leading-none">{app.stat.value}</div>
                <div className="mt-3 text-sm text-muted-foreground">{app.stat.label}</div>
              </div>
            )}
            <div className="mt-8 grid gap-2">
              {app.bullets.slice(0, 3).map((b, idx) => (
                <div key={b} className={`flex items-center justify-between rounded-xl ${muted ? "bg-muted" : "bg-card"} px-4 py-3 text-sm font-medium`}>
                  <span>{b}</span>
                  <span className="text-xs text-muted-foreground">0{idx + 1}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
